import { Seo } from "../components/Seo";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, Users, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function TeamAccept() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setInvalid("This invitation link is missing its token."); setLoading(false); return; }
    api.get(`/team/invite/${token}`)
      .then((r) => { setInvite(r.data); setName(r.data.name || ""); })
      .catch((e) => setInvalid(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/team/accept", { token, name, password });
      const user = setSession(data.access_token, data.user);
      toast.success("Welcome to the team!");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <Seo title="Accept team invitation" noindex />
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-14 w-auto object-contain" />
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400" data-testid="invite-loading"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : invalid ? (
          <div data-testid="invite-invalid" className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <h1 className="font-heading text-lg font-bold text-slate-900">Invitation unavailable</h1>
            <p className="text-sm text-slate-600 mt-1">{invalid}</p>
            <Link to="/login" className="mt-4 inline-block text-sm text-blue-600 hover:underline" data-testid="invite-login-link">Go to sign in</Link>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium px-3 py-1 mb-4">
              <Users className="h-3.5 w-3.5" /> Team invitation
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1" data-testid="invite-heading">Join the team</h1>
            <p className="text-sm text-slate-500 mb-6">
              <b className="text-slate-700">{invite.owner_name}</b> invited you to their <b className="text-slate-700">{invite.plan_name}</b> workspace on PNR Decode. Set a password to accept.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-slate-700">Email</Label>
                <Input data-testid="invite-email" value={invite.email} disabled className="mt-1 bg-slate-100 text-slate-500" />
              </div>
              <div>
                <Label htmlFor="name" className="text-slate-700">Your name</Label>
                <Input data-testid="invite-name" id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Create a password</Label>
                <Input data-testid="invite-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1" placeholder="At least 6 characters" />
              </div>
              {error && <div data-testid="invite-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
              <Button data-testid="invite-submit" type="submit" disabled={submitting || password.length < 6} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Accept & create account</>}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="invite-home-link">
                <ArrowLeft className="h-4 w-4" /> Go to homepage
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
